import type React from 'react'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from '../../../../Flux/Slice/index.js'
import { Actions } from '../../../../Flux/Slice/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { FileForProcessing, IContentFile } from '../../../../Model/index.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'

export interface FileModalProps {
  orderId: string
}

export const FileModal: React.FC<FileModalProps & BaseModalProps> = (props) => {
  const files = useSelector((state: typeof State) => state.order.fileList)
  const filesError = useSelector((state: typeof State) => state.order.fileListError)
  const dispatch = useDispatch()

  useEffect(() => {
    if (props.isOpen) {
      dispatch(Actions[orderSlice.name].loadFiles({ orderId: props.orderId }))
    }
  }, [props.isOpen])

  const categorizeFile = (file: { file: IContentFile; samples?: FileForProcessing[] }) => {
    if (!file.samples || file.samples.length === 0) return 'Unused'
    const sameOrder = file.samples.some((s) => s.orderId === props.orderId)
    const external = file.samples.some((s) => s.orderId !== props.orderId)
    if (external && sameOrder) return 'Used + External'
    if (external) return 'Used Externally'
    return 'Used'
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'Unused':
        return 'badge-ghost'
      case 'Used':
        return 'badge-success'
      case 'Used Externally':
        return 'badge-warning'
      case 'Used + External':
        return 'badge-info'
      default:
        return 'badge'
    }
  }

  return (
    <BaseModal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="modal-box max-w-[60vw] max-h-[80vh] min-h-[40vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold">
            <TooltipTrigger
              tooltip={
                'View files in this order. Files may be used within this order or also with different orders. Unused files in this order may prohibit order submission. Contact support for help with removing unused files.'
              }
              withIcon
            >
              Files attached to this order
            </TooltipTrigger>
          </h3>
          <button onClick={() => props.onClose()} className="btn btn-sm btn-circle">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {filesError ? (
          <div className="alert alert-error">Failed to load files for this order. Please try again later.</div>
        ) : !files ? (
          <div>Loading...</div>
        ) : files.length === 0 ? (
          <div className="text-base-content/60">No files are attached to this order.</div>
        ) : (
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {files
              .map((file) => {
                const status = categorizeFile(file)
                return { ...file, status }
              })
              .sort((a, b) => a.status.localeCompare(b.status)) // sort by status label
              .map(({ file, samples, status }) => (
                <div key={file.id} className="card shadow-sm border border-base-300 bg-base-100 p-4">
                  <div className="flex justify-between items-center">
                    <div className="truncate">
                      <p className="font-medium break-all">{file.fileName}</p>
                    </div>
                    <div className={`badge ${statusColor(status)}`}>{status}</div>
                  </div>
                  {samples && samples.length > 0 && (
                    <details className="mt-2 text-sm">
                      <summary className="cursor-pointer">Linked samples ({samples.length})</summary>
                      <ul className="list-disc list-inside ml-2 mt-1">
                        {samples.map((s) => (
                          <li key={s.id}>
                            Sample{' '}
                            <code>
                              <TooltipTrigger tooltip={s.id}>{s.customerSampleName ?? s.id}</TooltipTrigger>
                            </code>{' '}
                            (order: <code>{s.orderId}</code>)
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </BaseModal>
  )
}
